/**
 * Widerruf-Widget – Kernlogik (Vanilla JS, Shadow DOM, ES2019+).
 *
 * - Rendert den fix beschrifteten Button "Vertrag widerrufen".
 * - Oeffnet ein barrierefreies modales Formular (Fokus-Trap, Esc, aria-modal).
 * - Zweistufig: "Weiter zur Bestaetigung" -> Zusammenfassung -> "Widerruf bestaetigen".
 * - POST ausschliesslich an die makler-seitige Komponente (data-endpoint).
 *   Branding/Felder/Rechtstexte kommen per JSON-Init vom selben Endpoint.
 * - Cookie-/Tracker-frei (credentials:'omit', kein localStorage).
 * - Progressive Enhancement: faellt der JS-/Fetch-Weg aus, gibt es einen
 *   echten Link auf die serverseitige Strecke (No-JS).
 */

import { h, focusable } from "./dom.js";
import { STYLES } from "./styles.js";
import { validateValues, IDENT_ERROR_KEY } from "./validation.js";

// Gesetzlich/serverseitig fixierte Wortlaute – im Widget NICHT veraenderbar.
const FIXED_STEP1_TRIGGER = "Vertrag widerrufen";
const FIXED_STEP2_CONFIRM = "Widerruf bestätigen";

// Platzhalter-Schutz (identisch zur Server-Logik): solange nicht freigegeben,
// wird der rohe Platzhaltertext NICHT an Verbraucher ausgegeben.
const LEGAL_PLACEHOLDER_MARKER = "VOM_ANWALT_ZU_BEFUELLEN";
const SAFE_BELEHRUNG =
  "Die vollständige Widerrufsbelehrung stellen wir Ihnen auf Anfrage über die unten genannten Kontaktdaten bereit.";

const HEX_RE = /^#[0-9a-fA-F]{3,8}$/;

function hexToRgb(hex) {
  let h = String(hex || "").trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 8) h = h.slice(0, 6); // Alpha ignorieren
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function relLuminance(rgb) {
  const a = rgb.map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}
function contrastRatio(c1, c2) {
  const r1 = hexToRgb(c1);
  const r2 = hexToRgb(c2);
  if (!r1 || !r2) return 21;
  const l1 = relLuminance(r1);
  const l2 = relLuminance(r2);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}
/**
 * Liefert eine garantiert lesbare Button-Textfarbe gegen den Hintergrund.
 * Wunschfarbe wird genutzt, sofern Kontrast ausreichend; sonst Schwarz/Weiß –
 * die Textfarbe ist damit NIE gleich der Hintergrundfarbe.
 */
function readableOn(bg, desired) {
  const want = HEX_RE.test(desired || "") ? desired : "#ffffff";
  if (!hexToRgb(bg)) return want;
  if (contrastRatio(want, bg) >= 4.5) return want;
  return contrastRatio("#ffffff", bg) >= contrastRatio("#000000", bg) ? "#ffffff" : "#000000";
}

/** Anbieter-Kontaktblock (nur nicht-leere Zeilen) fuer die Belehrungs-Tokens. */
function contactBlock(c, inline) {
  c = c || {};
  const lines = [
    String(c.company || "").trim(),
    String(c.vertretung || "").trim(),
    String(c.address || "").trim(),
    c.phone ? "Telefon: " + String(c.phone).trim() : "",
    c.reply_to ? "E-Mail: " + String(c.reply_to).trim() : "",
  ].filter((s) => s !== "");
  return lines.join(inline ? " · " : "\n");
}
/** Fuellt Merge-Tokens der Belehrung aus den Kontaktdaten (identisch zur Server-Logik). */
function fillContactTokens(text, c) {
  if (!text) return text;
  const url = String((c && c.widerruf_url) || "").trim();
  return text
    .split("{{anbieter_kontakt_inline}}").join(contactBlock(c, true))
    .split("{{anbieter_kontakt}}").join(contactBlock(c, false))
    .split("{{widerruf_url}}").join(url !== "" ? url : "die Widerrufsfunktion auf unserer Website");
}

export class WiderrufWidget {
  constructor(opts) {
    this.key = opts.key;
    this.endpoint = opts.endpoint;
    this.overrides = opts.overrides || {};
    this.mountTarget = opts.mountTarget || null;
    this.inlineForm = opts.inlineForm === true; // true = Formular inline statt Modal
    this.cdn = opts.cdn || null; // Phase 2: zentraler Endpoint (Remote-Config/-Legal)

    this._brand = {}; // zusammengeführte Branding-Werte (über mehrere Quellen)

    this._notice = null;       // Renewal-Hinweis bei inaktiver Lizenz
    this._cdnApplied = false;  // zentrale Config bereits erfolgreich angewendet?
    this._cdnCache = null;     // letzter gültiger Stand (In-Session, kein localStorage)

    this.init = null; // Server-Init (branding/fields/legal/token)
    this.token = null;
    this.values = {};
    this.host = null;
    this.shadow = null;
    this.overlay = null;
    this.lastFocus = null;
    this.titleId = "wd-title-" + Math.random().toString(36).slice(2);

    this._onKey = this._onKey.bind(this);
  }

  /** Vollstaendige POST-Ziel-URL fuer GET-Init mit format=json. */
  _initUrl() {
    const sep = this.endpoint.indexOf("?") === -1 ? "?" : "&";
    return (
      this.endpoint + sep + "key=" + encodeURIComponent(this.key) + "&format=json"
    );
  }

  /** No-JS-Fallback-URL (volle serverseitige Strecke). */
  _fallbackUrl() {
    const sep = this.endpoint.indexOf("?") === -1 ? "?" : "&";
    return this.endpoint + sep + "key=" + encodeURIComponent(this.key);
  }

  async _fetchInit() {
    const res = await fetch(this._initUrl(), {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    if (!res.ok) throw new Error("init_http_" + res.status);
    const data = await res.json();
    if (!data || !data.ok) throw new Error("init_invalid");
    return data;
  }

  async _post(params) {
    const body = new URLSearchParams(params);
    const res = await fetch(this.endpoint, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "fetch",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body: body.toString(),
      credentials: "omit",
    });
    const data = await res.json().catch(() => ({ ok: false, error: "parse" }));
    return { status: res.status, data: data };
  }

  /* ----------------------------------------------------------------------
   * Phase 2: zentrale Remote-Config / Remote-Legal
   * -------------------------------------------------------------------- */

  _cdnBase() {
    return String(this.cdn || "").replace(/\/+$/, "");
  }

  _cdnConfigUrl() {
    return this._cdnBase() + "/config/" + encodeURIComponent(this.key) + ".json";
  }

  _cdnLegalUrl(catalog, version) {
    return this._cdnBase() + "/legal/" + catalog + "/v" + version + ".json";
  }

  async _fetchJson(url) {
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    if (!res.ok) throw new Error("http_" + res.status);
    return res.json();
  }

  /** Wandelt ein rohes Legal-JSON in die vom Widget genutzte Form (eine Variante). */
  _mapLegal(raw, variante, contact) {
    const vs = raw.belehrung_varianten || {};
    const v = vs[variante] || vs.mit_button || {};
    const released =
      raw.status === "FREIGEGEBEN" &&
      JSON.stringify(raw).indexOf(LEGAL_PLACEHOLDER_MARKER) === -1;
    const belehrung = released
      ? {
          label: v.label || "",
          body: fillContactTokens(v.body || "", contact),
          muster: fillContactTokens(v.muster_widerrufsformular || "", contact),
        }
      : { label: v.label || "", body: SAFE_BELEHRUNG, muster: "" };
    return {
      ui: raw.ui || {},
      verbraucher_hinweis: raw.verbraucher_hinweis || "",
      buttons_fixed: raw.buttons_fixed || { stufe1: "Vertrag widerrufen", stufe2: "Widerruf bestätigen" },
      belehrung: belehrung,
    };
  }

  async _applyRemoteConfig() {
    try {
      const cfg = await this._fetchJson(this._cdnConfigUrl());
      if (!cfg || cfg.key == null) throw new Error("config_invalid");

      // Renewal-Hook: inaktive Lizenz -> keine zentralen Updates, nur Hinweis.
      if (cfg.updates_enabled === false) {
        this._notice = cfg.notice || null;
        this._cdnApplied = true;
        return;
      }

      // Branding zentral uebernehmen.
      if (cfg.branding) {
        const b = cfg.branding;
        this.init.branding = Object.assign({}, this.init.branding, {
          primary: b.primary_color,
          btn_text: b.button_text_color,
          text: b.text_color,
          accent: b.accent_color || b.primary_color,
          font: b.font,
          logo: b.logo_url || "",
          position: b.position || "left",
        });
        this._applyBranding(this.init.branding);
      }

      // Anbieter-Kontakt (fuer Belehrungs-Tokens) aus der zentralen Config uebernehmen.
      if (cfg.contact) {
        this.init.contact = Object.assign({}, this.init.contact, cfg.contact);
      }

      // Rechtstexte zentral, versionsgepinnt laden.
      if (cfg.legal && cfg.legal.catalog && cfg.legal.version) {
        const raw = await this._fetchJson(this._cdnLegalUrl(cfg.legal.catalog, cfg.legal.version));
        this.init.legal = this._mapLegal(raw, cfg.legal.variante || "mit_button", this.init.contact);
      }

      this._cdnCache = { branding: this.init.branding, legal: this.init.legal };
      this._notice = null;
      this._cdnApplied = true;
    } catch (e) {
      // Graceful Degradation: zuletzt gueltiger Stand bleibt aktiv.
      if (this._cdnCache) {
        this.init.branding = this._cdnCache.branding;
        this.init.legal = this._cdnCache.legal;
        this._applyBranding(this.init.branding);
      }
      if (window.console) {
        console.warn("[widerruf] Zentrale Config nicht erreichbar – Fallback auf zuletzt gueltigen Stand.");
      }
    }
  }

  /* ----------------------------------------------------------------------
   * Mount: Trigger-Button (fix beschriftet)
   * -------------------------------------------------------------------- */
  mount() {
    this.host = document.createElement("div");
    this.host.setAttribute("data-widerruf-host", "");
    this.shadow = this.host.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = STYLES;
    this.shadow.appendChild(style);

    // Branding sofort aus den Overrides anwenden, damit Footer-Button UND
    // Formular von Anfang an konsistent gebrandet sind (gleiche Werte).
    this._applyBranding(this.overrides);

    if (this.inlineForm) {
      // Formular inline rendern (kein Trigger, kein Modal) – fuer Shortcode/Block.
      this.box = h("div", { class: "wd-box" });
      this.inner = h("div", { class: "wd-inner" });
      this.box.appendChild(this.inner);
      this.shadow.appendChild(this.box);
      (this.mountTarget || document.body).appendChild(this.host);
      this._ready = this._openInline(); // Promise (z. B. fuer Tests awaitbar)
      return;
    }

    const position = this.overrides.position === "right" ? "right" : "left";
    const inline = !!this.mountTarget;
    const btn = h("button", {
      type: "button",
      class: "wd-trigger " + (inline ? "wd-inline" : "wd-" + position),
      text: FIXED_STEP1_TRIGGER,
      "aria-haspopup": "dialog",
    });
    btn.addEventListener("click", () => this.openModal());
    this.shadow.appendChild(btn);
    this.trigger = btn;

    if (inline) {
      this.mountTarget.appendChild(this.host);
    } else {
      document.body.appendChild(this.host);
    }
  }

  /**
   * Wendet Branding konsistent an (Footer-Button UND Formular nutzen dieselben
   * CSS-Variablen). Werte aus mehreren Quellen (Overrides, Init, CDN) werden
   * gemerged. Akzeptiert sowohl primary/btn_text als auch primary_color/
   * button_text_color. Button-Textfarbe ist eigenständig und wird kontrastsicher
   * gesetzt (nie gleich der Hintergrundfarbe).
   */
  _applyBranding(b) {
    if (!this.shadow) return;
    b = b || {};
    const norm = {
      primary: b.primary || b.primary_color,
      btn_text: b.btn_text || b.button_text_color,
      text: b.text || b.text_color,
      accent: b.accent || b.accent_color,
      font: b.font,
      logo: b.logo !== undefined ? b.logo : b.logo_url,
      position: b.position,
    };
    for (const k in norm) {
      if (norm[k] != null && norm[k] !== "") this._brand[k] = norm[k];
    }

    const host = this.shadow.host;
    const isHex = (v) => HEX_RE.test(v || "");
    const primary = isHex(this._brand.primary) ? this._brand.primary : null;
    if (primary) host.style.setProperty("--wd-primary", primary);
    if (isHex(this._brand.text)) host.style.setProperty("--wd-text", this._brand.text);
    // Akzent-/Rahmenfarbe: eigene Einstellung, Default = Primärfarbe.
    const accent = isHex(this._brand.accent) ? this._brand.accent : primary;
    if (accent) host.style.setProperty("--wd-accent", accent);
    if (this._brand.font && /^[\w\s,'"\-]{1,120}$/.test(this._brand.font)) {
      host.style.setProperty("--wd-font", this._brand.font);
    }
    // Button-Textfarbe: eigene Farbe, garantiert lesbar (nie = Hintergrund).
    const bg = primary || "#1f2933";
    host.style.setProperty("--wd-btn-text", readableOn(bg, this._brand.btn_text));
  }

  /* ----------------------------------------------------------------------
   * Modal
   * -------------------------------------------------------------------- */
  async openModal() {
    this.lastFocus = document.activeElement;
    this._buildOverlay();
    await this._loadAndRenderStep1();
  }

  /** Inline-Modus (Shortcode/Block): Formular direkt im Zielelement rendern. */
  async _openInline() {
    await this._loadAndRenderStep1();
  }

  /** Gemeinsame Lade-/Render-Routine fuer Modal und Inline. */
  async _loadAndRenderStep1() {
    this._renderLoading();

    if (!this.init) {
      try {
        this.init = await this._fetchInit();
        this.token = this.init.token;
        this._applyBranding(this.init.branding);
      } catch (e) {
        this._renderFatal();
        return;
      }
    }
    // Phase 2: zentrale Remote-Config/-Legal best-effort anwenden.
    // Graceful Degradation: schlaegt der CDN-Abruf fehl, bleibt der Backend-Init-
    // bzw. der zuletzt gueltige Stand aktiv (Pflichtfunktion bricht nie).
    if (this.cdn && !this._cdnApplied) {
      await this._applyRemoteConfig();
    }
    // Erklaerung vorbefuellen (Default aus Legal-UI), nur beim ersten Mal.
    if (!this.values.erklaerung) {
      const ui = (this.init.legal && this.init.legal.ui) || {};
      this.values.erklaerung = ui.default_erklaerung || "";
    }
    this._renderStep1();
  }

  _buildOverlay() {
    this.overlay = h("div", {
      class: "wd-overlay",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": this.titleId,
    });
    this.box = h("div", { class: "wd-box" });
    const close = h("button", {
      type: "button",
      class: "wd-close",
      "aria-label": "Schließen",
      html: "&times;",
    });
    close.addEventListener("click", () => this.closeModal());
    this.box.appendChild(close);
    this.inner = h("div", { class: "wd-inner" });
    this.box.appendChild(this.inner);
    this.overlay.appendChild(this.box);

    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.closeModal();
    });
    document.addEventListener("keydown", this._onKey);
    this.shadow.appendChild(this.overlay);
    close.focus();
  }

  _onKey(e) {
    if (!this.overlay) return;
    if (e.key === "Escape") {
      this.closeModal();
      return;
    }
    if (e.key === "Tab") {
      // Fokus-Trap
      const items = focusable(this.overlay);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = this.shadow.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  closeModal() {
    if (!this.overlay) return;
    document.removeEventListener("keydown", this._onKey);
    if (this.overlay.parentNode) this.overlay.parentNode.removeChild(this.overlay);
    this.overlay = null;
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    else if (this.trigger) this.trigger.focus();
  }

  _clearInner() {
    while (this.inner.firstChild) this.inner.removeChild(this.inner.firstChild);
  }

  _renderLoading() {
    this._clearInner();
    this.inner.appendChild(
      h("div", { class: "wd-loading", text: "Wird geladen …" })
    );
  }

  _renderFatal() {
    this._clearInner();
    const box = h("div", { class: "wd-errbox" });
    box.appendChild(
      h("p", {
        text:
          "Das Widerrufsformular konnte nicht geladen werden. Bitte nutzen Sie den folgenden Link:",
      })
    );
    box.appendChild(
      h("a", {
        href: this._fallbackUrl(),
        text: "Zum Widerrufsformular",
        rel: "noopener",
      })
    );
    this.inner.appendChild(box);
    const items = focusable(this.overlay || this.box);
    if (items.length) items[0].focus();
  }

  /* ----------------------------------------------------------------------
   * Schritt 1 – Formular
   * -------------------------------------------------------------------- */
  _renderStep1(errors) {
    this._clearInner();
    errors = errors || {};
    const legal = this.init.legal || {};
    const ui = legal.ui || {};
    const fields = this.init.fields || [];
    const identKeys = this.init.identKeys || [];

    if (this.init.branding && this.init.branding.logo) {
      this.inner.appendChild(
        h("img", {
          class: "wd-logo",
          src: this.init.branding.logo,
          alt: this.init.company || "",
        })
      );
    }

    this.inner.appendChild(
      h("h1", { class: "wd-h1", id: this.titleId, text: ui.title || FIXED_STEP1_TRIGGER })
    );
    if (ui.intro) this.inner.appendChild(h("p", { class: "wd-sub", text: ui.intro }));

    // Phase 2: Renewal-Hinweis (inaktive Lizenz) – informativ, bricht die Funktion nicht.
    if (this._notice) {
      this.inner.appendChild(h("div", { class: "wd-hint", role: "status", text: this._notice }));
    }

    // Verbraucher-/Gewerbe-Hinweis + einklappbare Belehrung + Anbieter-Kontakt
    if (legal.verbraucher_hinweis) {
      // pre-line: erhält Absätze (\n) des allgemeinen Hinweises.
      this.inner.appendChild(h("p", { class: "wd-sub", style: "white-space:pre-line", text: legal.verbraucher_hinweis }));
    }
    this.inner.appendChild(this._belehrung(legal, ui));

    // Pflichtfeld-Hinweis
    this.inner.appendChild(
      h("p", { class: "wd-hint", text: ui.required_hint || "Mit * markierte Felder sind Pflichtfelder." })
    );

    if (Object.keys(errors).length) {
      this.inner.appendChild(
        h("div", { class: "wd-errbox", role: "alert", text: "Bitte prüfen Sie die markierten Felder." })
      );
    }

    const form = h("form", { novalidate: "", autocomplete: "on" });

    // Honeypot
    const hp = h("div", { class: "wd-hp", "aria-hidden": "true" });
    hp.appendChild(h("label", { text: "Website (bitte leer lassen)" }));
    this._hp = h("input", { type: "text", name: "website", tabindex: "-1", autocomplete: "off" });
    hp.appendChild(this._hp);
    form.appendChild(hp);

    this._inputs = {};
    let identOpen = null;
    for (const f of fields) {
      const isIdent = f.group === "ident" || (f.group && f.group === IDENT_ERROR_KEY) || identKeys.indexOf(f.key) !== -1;

      if (isIdent && !identOpen) {
        identOpen = h("fieldset", { class: "wd-ident" });
        identOpen.appendChild(h("legend", { text: "Vertrag identifizieren" }));
        identOpen.appendChild(
          h("p", { class: "wd-hint", text: ui.either_or_label || "Bitte mindestens eines dieser Felder ausfüllen." })
        );
        if (errors[IDENT_ERROR_KEY]) {
          identOpen.appendChild(h("p", { class: "wd-err", role: "alert", text: errors[IDENT_ERROR_KEY] }));
        }
        form.appendChild(identOpen);
      }

      const container = isIdent ? identOpen : form;
      container.appendChild(this._field(f, errors));
    }

    const actions = h("div", { class: "wd-actions" });
    const submit = h("button", {
      type: "submit",
      class: "wd-btn",
      text: ui.step1_submit || "Weiter zur Bestätigung",
    });
    actions.appendChild(submit);
    form.appendChild(actions);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      this._submitStep1();
    });

    this.inner.appendChild(form);

    // Fokus auf erstes Feld (A11y).
    const firstInput = fields.length ? this._inputs[fields[0].key] : null;
    if (firstInput) firstInput.focus();
  }

  _belehrung(legal, ui) {
    const det = h("details", { class: "wd-belehrung" });
    det.appendChild(h("summary", { text: ui.belehrung_toggle || "Widerrufsbelehrung & Anbieter anzeigen" }));
    const wrap = h("div");
    const bel = (legal.belehrung && legal.belehrung.body) || "";
    const muster = (legal.belehrung && legal.belehrung.muster) || "";

    // Klar voneinander getrennte Abschnitte (eigene Überschrift je Abschnitt):
    //   1) Widerrufsbelehrung  2) Muster-Widerrufsformular  3) Anbieter/formloser Weg.
    const section = (cls, heading, bodyNode) => {
      const sec = h("section", { class: "wd-legal-sec" + (cls ? " " + cls : "") });
      sec.appendChild(h("h2", { class: "wd-legal-h", text: heading }));
      sec.appendChild(bodyNode);
      return sec;
    };

    // 1) Widerrufsbelehrung
    if (bel) {
      wrap.appendChild(section(
        "",
        ui.belehrung_heading || "Widerrufsbelehrung",
        h("div", { class: "wd-belehrung-body", text: bel })
      ));
    }
    // 2) Muster-Widerrufsformular – deutlich abgesetzt (eigener, umrahmter Block).
    if (muster) {
      wrap.appendChild(section(
        "wd-muster",
        ui.muster_heading || "Muster-Widerrufsformular",
        h("div", { class: "wd-belehrung-body", text: muster })
      ));
    }
    // 3) Anbieter & formloser Weg
    const anbieter = h("div");
    anbieter.appendChild(
      h("p", { class: "wd-sub", style: "margin:0 0 6px", text: ui.alt_channel_intro || "Sie können den Widerruf auch formlos – z. B. per Brief oder E-Mail – an die folgenden Kontaktdaten richten:" })
    );
    const c = this.init.contact || {};
    const lines = [c.company, c.address, c.reply_to].filter(Boolean);
    anbieter.appendChild(h("div", { class: "wd-contact", text: lines.join(" · ") }));
    wrap.appendChild(section("", ui.anbieter_heading || "Anbieter & formloser Widerruf", anbieter));

    det.appendChild(wrap);
    return det;
  }

  _field(f, errors) {
    const frag = document.createDocumentFragment();
    const id = "wd-f-" + f.key;
    const label = h("label", { for: id });
    label.appendChild(document.createTextNode(f.label));
    if (f.required) {
      label.appendChild(h("span", { class: "wd-req", "aria-hidden": "true", text: " *" }));
    }
    frag.appendChild(label);

    const hasErr = !!errors[f.key];
    let input;
    const common = {
      id: id,
      name: f.key,
      autocomplete: f.autocomplete || "off",
    };
    if (hasErr) {
      common["aria-invalid"] = "true";
      common["aria-describedby"] = "wd-e-" + f.key;
    }
    if (f.type === "textarea") {
      input = h("textarea", common);
      input.value = this.values[f.key] || "";
    } else {
      input = h("input", Object.assign(common, { type: f.type || "text" }));
      if (f.required) input.required = true;
      input.value = this.values[f.key] || "";
    }
    input.addEventListener("input", () => {
      this.values[f.key] = input.value;
    });
    this._inputs[f.key] = input;
    frag.appendChild(input);

    if (hasErr) {
      frag.appendChild(h("p", { class: "wd-err", id: "wd-e-" + f.key, role: "alert", text: errors[f.key] }));
    }
    return frag;
  }

  _collect() {
    const v = {};
    for (const k in this._inputs) v[k] = this._inputs[k].value;
    this.values = Object.assign({}, this.values, v);
    return v;
  }

  async _submitStep1() {
    const values = this._collect();
    const fields = this.init.fields || [];
    const identKeys = this.init.identKeys || [];

    // 1) Client-Validierung (Komfort, sofortiges Feedback).
    const clientErrors = validateValues(values, fields, identKeys);
    if (Object.keys(clientErrors).length) {
      this._renderStep1(clientErrors);
      return;
    }

    // 2) Server-Validierung (massgeblich) + Zusammenfassung.
    const params = this._params({ step: "1" });
    let r = await this._post(params).catch(() => null);
    if (r && r.data && r.data.error === "token" && r.data.newToken) {
      this.token = r.data.newToken;
      r = await this._post(this._params({ step: "1" })).catch(() => null);
    }
    if (!r) {
      this._renderStep1({ ident: "Verbindung fehlgeschlagen. Bitte erneut versuchen." });
      return;
    }
    if (r.data.error === "validation") {
      this._renderStep1(r.data.errors || {});
      return;
    }
    if (r.data.ok && r.data.step === 2) {
      this._renderStep2(r.data.summary || this._localSummary());
      return;
    }
    this._renderStep1({ ident: r.data.message || "Unerwarteter Fehler. Bitte erneut versuchen." });
  }

  _localSummary() {
    const out = [];
    for (const f of this.init.fields || []) {
      const val = this.values[f.key];
      if (val && String(val).trim() !== "") out.push({ label: f.label, value: val });
    }
    return out;
  }

  /* ----------------------------------------------------------------------
   * Schritt 2 – Zusammenfassung & Bestaetigung
   * -------------------------------------------------------------------- */
  _renderStep2(summary) {
    this._clearInner();
    const ui = (this.init.legal && this.init.legal.ui) || {};

    this.inner.appendChild(
      h("h1", { class: "wd-h1", id: this.titleId, text: ui.step2_title || FIXED_STEP2_CONFIRM })
    );
    if (ui.step2_intro) this.inner.appendChild(h("p", { class: "wd-sub", text: ui.step2_intro }));

    const sum = h("div", { class: "wd-summary" });
    for (const row of summary) {
      const r = h("div", { class: "wd-row" });
      r.appendChild(h("span", { class: "wd-k", text: row.label }));
      r.appendChild(h("span", { text: row.value }));
      sum.appendChild(r);
    }
    this.inner.appendChild(sum);

    const actions = h("div", { class: "wd-actions" });
    const back = h("button", { type: "button", class: "wd-btn wd-btn-secondary", text: ui.step2_back || "Zurück" });
    back.addEventListener("click", () => this._renderStep1());
    const confirm = h("button", { type: "button", class: "wd-btn", text: FIXED_STEP2_CONFIRM });
    confirm.addEventListener("click", () => {
      confirm.disabled = true;
      this._submitStep2(confirm);
    });
    actions.appendChild(back);
    actions.appendChild(confirm);
    this.inner.appendChild(actions);

    back.focus();
  }

  async _submitStep2(confirmBtn) {
    let r = await this._post(this._params({ step: "2" })).catch(() => null);
    if (r && r.data && r.data.error === "token" && r.data.newToken) {
      this.token = r.data.newToken;
      r = await this._post(this._params({ step: "2" })).catch(() => null);
    }
    if (!r) {
      if (confirmBtn) confirmBtn.disabled = false;
      this._renderStep2(this._localSummary());
      this.inner.insertBefore(
        h("div", { class: "wd-errbox", role: "alert", text: "Verbindung fehlgeschlagen. Bitte erneut bestätigen." }),
        this.inner.firstChild.nextSibling
      );
      return;
    }
    if (r.data.error === "rate_limited") {
      this._renderResult(false, "", "", r.data.message);
      return;
    }
    if (r.data.error === "validation") {
      this._renderStep1(r.data.errors || {});
      return;
    }
    if (r.data.ok && r.data.done) {
      this._renderResult(r.data.mail_ok, r.data.case_id, r.data.email, r.data.message);
      return;
    }
    if (confirmBtn) confirmBtn.disabled = false;
    this._renderStep2(this._localSummary());
  }

  _renderResult(mailOk, caseId, email, message) {
    this._clearInner();
    if (mailOk) {
      const box = h("div", { class: "wd-ok" });
      box.appendChild(h("strong", { text: "Ihr Widerruf ist eingegangen." }));
      box.appendChild(h("br"));
      box.appendChild(document.createTextNode("Vorgangsnummer: " + (caseId || "")));
      if (email) {
        box.appendChild(h("br"));
        box.appendChild(
          document.createTextNode("Eine Eingangsbestätigung (inkl. PDF) wurde an " + email + " gesendet.")
        );
      }
      this.inner.appendChild(box);
    } else {
      this.inner.appendChild(
        h("div", {
          class: "wd-errbox",
          role: "alert",
          text:
            message ||
            "Ihr Widerruf wurde erfasst" +
              (caseId ? " (Vorgangsnummer " + caseId + ")" : "") +
              ", die Bestätigungs-Mail konnte jedoch nicht versendet werden.",
        })
      );
    }
    // Pflicht-Vorbehalt: immer anzeigen (unabhängig von Anwaltstexten), wie in E-Mail/PDF
    this.inner.appendChild(
      h("p", {
        class: "wd-sub",
        style: "margin-top:12px",
        text:
          "Dies ist ausschließlich eine Bestätigung des Eingangs Ihrer Erklärung. " +
          "Ob Ihr Widerruf wirksam ist und welche Reichweite er hat, wird von uns noch geprüft. " +
          "Über das Ergebnis informieren wir Sie gegebenenfalls gesondert.",
      })
    );
    // "Schließen" nur im Modal-Modus (inline gibt es kein Overlay zu schließen).
    if (this.overlay) {
      const actions = h("div", { class: "wd-actions" });
      const done = h("button", { type: "button", class: "wd-btn", text: "Schließen" });
      done.addEventListener("click", () => this.closeModal());
      actions.appendChild(done);
      this.inner.appendChild(actions);
      done.focus();
    }
  }

  _params(extra) {
    const p = Object.assign({}, this.values, extra, {
      key: this.key,
      token: this.token || "",
      website: this._hp ? this._hp.value : "",
    });
    return p;
  }
}
